import classNames from "classnames"
import { format, parseISO } from "date-fns"
import pluralize from "pluralize"
import React, { useContext, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"

import Badge from "src/components/Badge"
import Button from "src/components/Button"
import Chips from "src/components/Chips"
import DataTable from "src/components/DataTable"
import Form from "src/components/Form"
import Loader from "src/components/Loader"
import Tooltip from "src/components/Tooltip"

import { queryApideckInvoices, retryInvoiceSync } from "src/api/Apideck"

import useDebounce from "src/hooks/use_debounce"
import { useToast } from "src/hooks/use_toast"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { snakecaseToCamelCase, titlecase } from "src/utils/string_helpers"
import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

import { AccountingContext } from "./AccountingView"
import { SYNC_STATUSES } from "./constants"

const { EXCLUDED, FAILED } = SYNC_STATUSES
const GUEST = "Guest"
const INACTIVE = "inactive"
const PAGE_SIZE = 25

const SyncTab = () => {
  const navigate = useNavigate()
  const { integration, marinaSlug } = useContext(AccountingContext)
  const queryClient = useQueryClient()
  const showToast = useToast()
  const urlParams = new URLSearchParams(window.location.search)
  const [filters, setFilters] = useState({
    page: urlParams.get("page") || 1,
    searchTerm: urlParams.get("search_term") || "",
    sortDirection: urlParams.get("sort_direction") || "asc",
    sortKey: urlParams.get("sort_key")
      ? snakecaseToCamelCase(urlParams.get("sort_key"))
      : "syncStatus",
  })
  const { searchTerm, sortDirection, sortKey } = filters
  const hasFiltersApplied = !!searchTerm
  const [debouncedQuery] = useDebounce(setFilters)

  const updateParams = (params) => {
    debouncedQuery(params)
    updateUrlQueryParams({
      search_term: params.searchTerm,
      sort_key: params.sortKey,
      sort_direction: params.sortDirection,
      page: params.page,
    })
  }

  const handleColumnSort = (key) => {
    let newSortDirection = null
    let newSortKey = key
    if (sortKey === key) {
      if (sortDirection === "asc") {
        newSortDirection = "desc"
      } else {
        newSortKey = null
      }
    } else {
      newSortDirection = "asc"
    }

    updateParams({
      ...filters,
      sortKey: newSortKey,
      sortDirection: newSortDirection,
      page: 1,
    })
  }

  const handleSearch = (event) => {
    setValue("searchTerm", event.target.value)
    updateParams({ ...filters, searchTerm: event.target.value.trim(), page: 1 })
  }

  const { register, setValue, watch } = useForm({
    defaultValues: {
      searchTerm,
    },
  })

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "pending":
        return "blue"
      case "excluded":
        return "yellow"
      case "failed":
        return "red"
      case "success":
        return "teal"
      default:
        return "gray"
    }
  }

  const COLDEFS = [
    {
      key: "syncStatus",
      header: "Status",
      render: (data) => (
        <Badge
          color={getStatusBadgeColor(data.syncStatus)}
          text={titlecase(data.syncStatus)}
        />
      ),
      sortable: true,
      width: "8%",
    },
    {
      key: "invoice",
      header: "Invoice",
      render: (data) => (
        <a
          href={data.parentObjectPath}
          className="cursor-pointer text-blue-600"
          rel="noreferrer noopener"
          target="_blank"
        >
          <span className="block truncate">{data.billingInvoiceId}</span>
        </a>
      ),
      width: "20%",
    },
    {
      key: "contact",
      header: "Customer",
      render: (data) => data.contact?.name || GUEST,
      sortable: true,
      width: "13%",
    },
    {
      key: "updatedAt",
      header: "Updated",
      render: (data) => (
        <span className="block truncate">
          {format(parseISO(data.updatedAt), "MM/dd/yyyy hh:mm aa")}
        </span>
      ),
      sortable: true,
      width: "13%",
    },
    {
      key: "syncedAt",
      header: "Last Synced",
      render: (data) =>
        data.syncedAt ? (
          <span className="block truncate">
            {format(parseISO(data.syncedAt), "MM/dd/yyyy hh:mm aa")}
          </span>
        ) : null,
      sortable: true,
      width: "13%",
    },
    {
      key: "amount",
      header: "Amount",
      render: (data) => formatCurrencyFromMinorUnits({ amount: data.amount }),
      sortable: true,
      width: "8%",
    },
    {
      key: "errors",
      header: "",
      render: (data) => {
        const errorCount = data.syncErrors.length
        if (errorCount === 0) return undefined

        const actionable = data.syncErrors.filter((error) => error.actionable)
        const nonActionable = data.syncErrors.filter(
          (error) => !error.actionable
        )
        // Headings only earn their space when both kinds are present — otherwise the count beside
        // the icon already says which kind this row is, and a lone heading is just noise.
        const grouped = actionable.length > 0 && nonActionable.length > 0
        const bulleted = errorCount > 1

        const errorLines = (errors) =>
          errors.map((error, index) => (
            <div key={index}>{`${bulleted ? "- " : ""}${error.code}: ${
              error.displayMessage
            }`}</div>
          ))

        return (
          <div className="flex items-center whitespace-nowrap font-semibold text-red-600">
            <Tooltip
              text={
                <div className="space-y-3">
                  {grouped ? (
                    <>
                      <div>
                        <div className="font-semibold">Action required</div>
                        {errorLines(actionable)}
                      </div>
                      <div>
                        <div className="font-semibold">No action needed</div>
                        {errorLines(nonActionable)}
                      </div>
                    </>
                  ) : (
                    errorLines(data.syncErrors)
                  )}
                </div>
              }
              variant="dark"
              placement="bottom"
              maxWidth="400px"
              tabIndex={0}
            >
              <i className="fa-kit fa-exclamation-circle pr-1 text-base font-semibold" />
            </Tooltip>
            <div className="block truncate">
              {actionable.length > 0
                ? `${actionable.length} ${pluralize(
                    "action",
                    actionable.length
                  )} required`
                : `${errorCount} sync ${pluralize("error", errorCount)}`}
            </div>
          </div>
        )
      },
      width: "15%",
    },
    {
      key: "retry",
      header: "",
      render: (data) => (
        <Button
          disabled={
            integration.status === INACTIVE ||
            (data.syncStatus !== FAILED && data.syncStatus !== EXCLUDED)
          }
          onClick={() => retryInvoice(data.id)}
        >
          Retry
        </Button>
      ),
      cellAlign: "right",
      width: "8%",
    },
  ]

  const sortConfig = {
    sortKey: sortKey ? snakecaseToCamelCase(sortKey) : null,
    sortDirection: sortDirection || null,
    onColumnSort: handleColumnSort,
  }

  const {
    isFetching: isLoadingInvoices,
    data: invoicesData,
    isError: isErrorLoadingInvoices,
  } = useQuery({
    queryKey: ["invoices", marinaSlug, filters, integration],
    queryFn: () =>
      queryApideckInvoices({
        marinaSlug,
        filters,
        pageSize: PAGE_SIZE,
      }),
    refetchOnWindowFocus: false,
    enabled: integration.setupComplete,
    retry: false,
  })

  const { mutate: retryInvoice } = useMutation(
    (id) => retryInvoiceSync({ marinaSlug, id }),
    {
      onSuccess: (data) => {
        const { invoice: updatedInvoice } = data
        showToast(
          `Retry of invoice #${updatedInvoice.billingInvoiceId} initiated successfully`,
          { type: "success" }
        )
        queryClient.setQueryData(
          ["invoices", marinaSlug, filters, integration],
          () => {
            return {
              ...invoicesData,
              items: invoicesData.items.map((existingInvoice) =>
                existingInvoice.id === updatedInvoice.id
                  ? updatedInvoice
                  : existingInvoice
              ),
            }
          }
        )
      },
      onError: (error) => {
        showToast(error.message, {
          type: "error",
        })
      },
    }
  )

  const renderSearchInput = () => (
    <Form.IconTextField
      {...register("searchTerm")}
      icon={<i className="fa-kit fa-search-mdi text-xl text-gray-400" />}
      id="invoice-item-search"
      isClearable
      onChange={handleSearch}
      onClearSelection={() => {
        setValue("searchTerm", "")
        updateParams({ ...filters, searchTerm: "" })
      }}
      position="left"
      value={watch("searchTerm")}
      placeholder="Search by customer name/email or the ID of an invoice, reservation, contract, or sale"
    />
  )

  const renderFilterChips = () => {
    return (
      <div className="flex flex-wrap">
        {searchTerm && (
          <Chips.Chip
            label="Search"
            text={`Search: ${searchTerm}`}
            onClick={() => {
              setValue("searchTerm", "")
              updateParams({ ...filters, searchTerm: "", page: 1 })
            }}
          />
        )}
        {hasFiltersApplied && (
          <div className="flex w-min items-center">
            <Button
              variant="ghost"
              small
              onClick={() => {
                setValue("searchTerm", "")
                updateParams({ ...filters, searchTerm: "", page: 1 })
              }}
            >
              Reset
            </Button>
          </div>
        )}
      </div>
    )
  }

  const renderTableEmptyState = (variant) => {
    let title
    let subtitle

    if (variant === "incompleteSetup") {
      title = "Syncing not yet started"
      subtitle =
        "Please complete setup and ensure your integration status is 'on'."
    } else if (variant === "invoicesDataEmpty") {
      title = "No invoices available"
      subtitle = hasFiltersApplied
        ? "There are no invoices that match your search, please try adjusting your input."
        : "There are no synced invoices available for review at this time."
    } else if (variant === "error") {
      title = "Error loading invoices"
      subtitle =
        "Please try reloading the page or contact Dockwa support at mayday@dockwa.com"
    }

    return (
      <div className="card my-5 flex min-h-96 flex-col justify-center text-center">
        {title ? (
          <div
            className={classNames(
              { "text-red-600": variant === "error" },
              "font-bold"
            )}
          >
            {title}
          </div>
        ) : null}
        {subtitle ? <div className="text-muted text-sm">{subtitle}</div> : null}
        {variant === "loading" ? <Loader name="invoices" /> : null}
        {variant === "incompleteSetup" ? (
          <div className="mt-2">
            <Button variant="primary" onClick={() => navigate("/setup")}>
              Go to Setup
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  const renderTable = () => {
    if (!integration.setupComplete) {
      return renderTableEmptyState("incompleteSetup")
    } else if (isErrorLoadingInvoices) {
      return renderTableEmptyState("error")
    } else if (isLoadingInvoices) {
      return renderTableEmptyState("loading")
    } else if (invoicesData?.items?.length === 0) {
      return renderTableEmptyState("invoicesDataEmpty")
    } else {
      return (
        <DataTable
          name="invoice"
          isLoading={isLoadingInvoices}
          colDefs={COLDEFS}
          rowData={invoicesData?.items}
          page={invoicesData?.page}
          numberOfPages={invoicesData?.page_count}
          onPageChange={(page) =>
            updateParams({ ...filters, page: page.selected })
          }
          sortConfig={sortConfig}
          fullHeight
          hideActions
          hideSettings
          pagination
        />
      )
    }
  }

  return (
    <div className="mb-[250px]">
      <h4 className="mb-1 pt-6 font-semibold text-gray-900">Sync Details</h4>
      <div className="mb-8">
        <span className="text-gray-800">Log of recent sync activity.</span>
      </div>
      <div className="w-full pb-2 xl:mr-2 xl:w-[610px] xl:p-0">
        {renderSearchInput()}
      </div>
      <div className="px-4 xl:px-0">
        <Chips.List>{renderFilterChips()}</Chips.List>
      </div>
      <div className="my-8">{renderTable()}</div>
    </div>
  )
}

export default SyncTab
